import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS, PHONE } from "@/lib/site";
import { Loader2, PhoneCall } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "h-12 rounded-none bg-black border border-[#222] text-white placeholder:text-[#666] focus-visible:ring-1 focus-visible:ring-[#FF4D00] focus-visible:ring-offset-0 focus-visible:border-[#FF4D00]";

export default function QuoteDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service_type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email || null, service_type: form.service_type || null };
      await axios.post(`${API}/quote`, payload);
      toast.success("Quote request sent! The Guys will call you shortly.");
      setForm({ name: "", phone: "", email: "", service_type: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + PHONE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="bg-[#0a0a0a] border border-[#222] rounded-none sm:max-w-[520px] p-0 max-h-[90vh] overflow-y-auto"
      >
        <div className="bg-[#FF4D00] px-6 py-3">
          <p className="font-mono-a text-[11px] tracking-[0.2em] uppercase text-black">No obligation</p>
        </div>
        <DialogHeader className="px-6 pt-5">
          <DialogTitle className="font-display text-3xl uppercase tracking-tight text-white text-left">
            Get Your Free Quote
          </DialogTitle>
          <p className="text-[#888] text-sm text-left mt-1">
            Tell us what needs to go. We'll call with an upfront price — usually within the hour.
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="px-6 pb-6 pt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Name *</Label>
              <Input data-testid="quote-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Phone *</Label>
              <Input data-testid="quote-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(720) 000-0000" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Email</Label>
              <Input data-testid="quote-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Service</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
                <SelectTrigger data-testid="quote-service" className={field}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-[#222] text-white rounded-none">
                  {SERVICE_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o} className="focus:bg-[#FF4D00] focus:text-black rounded-none">{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">What needs to go?</Label>
            <Textarea data-testid="quote-message" className={`${field} h-24 py-3`} value={form.message} onChange={set("message")} placeholder="e.g. old sofa, fridge and a garage full of boxes" />
          </div>
          <button
            data-testid="quote-submit"
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#FF4D00] hover:bg-[#FF6A00] text-black font-mono-a uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
            {loading ? "Sending..." : "Request Free Quote"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
